import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBoards } from '@/entities/board';
import {
  type BoardLabelEntry,
  type Impact,
  ImpactSummary,
  useAssignBoardLabel,
  useCreateBoardLabel,
  useDeleteBoardLabel,
  useLabelBoardIds,
  useUpdateBoardLabel,
} from '@/entities/collab';
import type { Workspace } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { can, PERMISSIONS } from '@/shared/lib/permissions';
import { Button, ColorSwatches, ConfirmDialog, Dialog, MultiSelect, TextField } from '@/shared/ui';

import styles from './LabelEditor.module.css';

const DEFAULT_COLOR = '#2860cc';

/**
 * A compact editor for ONE board label (a modal): name, color and the boards that carry it.
 * It closes on Save, Cancel, Escape and a successful delete; a failed request keeps it open
 * with the error. Changes that grant or revoke access are previewed before they are applied.
 */
export function LabelEditor({
  workspace,
  label,
  onClose,
}: {
  workspace: Workspace;
  label: BoardLabelEntry | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const canManage = can(workspace.permissions, PERMISSIONS.workspaceManageBoardLabels);
  const boards = useBoards(workspace.id);
  const carrying = useLabelBoardIds(workspace.id, label?.id ?? null);
  const create = useCreateBoardLabel(workspace.id);
  const update = useUpdateBoardLabel(workspace.id);
  const assign = useAssignBoardLabel(workspace.id);
  const remove = useDeleteBoardLabel(workspace.id);
  const [name, setName] = useState(label?.name ?? '');
  const [color, setColor] = useState(label?.color ?? DEFAULT_COLOR);
  const [picked, setPicked] = useState<string[] | null>(null);
  const [preview, setPreview] = useState<Impact | null>(null);
  const [deleting, setDeleting] = useState<Impact | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const had = carrying.data ?? [];
  const selected = picked ?? had;
  const toAdd = selected.filter((id) => !had.includes(id));
  const toRemove = had.filter((id) => !selected.includes(id));
  const boardChanges = toAdd.length + toRemove.length > 0;
  const trimmed = name.trim();
  const identityChanged = label === null || trimmed !== label.name || color !== label.color;
  const dirty = identityChanged || boardChanges;
  const controlsAccess = label?.access_controlling ?? false;

  const assignAll = async (labelId: string, dryRun: boolean): Promise<Impact | null> => {
    let last: Impact | null = null;
    for (const [ids, removeIt] of [
      [toAdd, false],
      [toRemove, true],
    ] as const) {
      if (ids.length === 0) continue;
      const result = await assign.mutateAsync({ labelId, boardIds: ids, remove: removeIt, dryRun });
      last = result.impact;
    }
    return last;
  };

  const save = async () => {
    setError(null);
    setBusy(true);
    try {
      // Access-changing assignments are shown first and applied on the second press.
      if (label && controlsAccess && boardChanges && preview === null) {
        setPreview(await assignAll(label.id, true));
        return;
      }
      let labelId = label?.id ?? '';
      if (label === null) {
        labelId = (await create.mutateAsync({ name: trimmed, color })).id;
      } else if (trimmed !== label.name || color !== label.color) {
        await update.mutateAsync({ labelId, name: trimmed, color });
      }
      if (boardChanges) await assignAll(labelId, false);
      onClose();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={
        label ? t('workspaceLabels.editTitle', { name: label.name }) : t('workspaceAdmin.addLabel')
      }
      onClose={onClose}
    >
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (canManage && trimmed && dirty && !busy) void save();
        }}
      >
        <TextField
          label={t('workspaceAdmin.labelName')}
          value={name}
          maxLength={40}
          autoFocus
          disabled={!canManage}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <ColorSwatches
          label={t('workspaceAdmin.labelColor')}
          value={color}
          onChange={(value) => {
            if (value && canManage) setColor(value);
          }}
        />
        <div className={styles.field}>
          <span className={styles.fieldLabel}>{t('workspaceAdmin.assignToBoards')}</span>
          <MultiSelect
            label={t('workspaceLabels.boards')}
            searchPlaceholder={t('workspaceAdmin.searchBoards')}
            disabled={!canManage}
            options={(boards.data ?? []).map((board) => ({ value: board.id, label: board.name }))}
            value={selected}
            onChange={(next) => {
              setPicked(next);
              setPreview(null);
            }}
          />
          <p className={styles.hint}>
            {controlsAccess ? t('workspaceLabels.accessHint') : t('workspaceAdmin.assignHint')}
          </p>
        </div>
        {preview && <ImpactSummary impact={preview} />}
        <FormError error={error ?? remove.error} />
        <div className={styles.actions}>
          {label && canManage && (
            <Button
              variant="danger"
              onClick={() => {
                remove.mutate(
                  { labelId: label.id, dryRun: true },
                  {
                    onSuccess: (impact) => {
                      setDeleting(impact);
                    },
                  },
                );
              }}
            >
              {t('common.delete')}
            </Button>
          )}
          <span className={styles.spacer} />
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          {canManage && (
            <Button type="submit" variant="primary" disabled={!trimmed || !dirty || busy}>
              {preview ? t('workspaceLabels.apply') : t('common.save')}
            </Button>
          )}
        </div>
      </form>
      {deleting && label && (
        <ConfirmDialog
          title={t('workspaceAdmin.deleteLabelTitle', { name: label.name })}
          message={
            deleting.gained + deleting.lost + deleting.changed > 0
              ? t('workspaceAdmin.deleteLabelImpact', {
                  lost: deleting.lost,
                  gained: deleting.gained,
                })
              : t('workspaceAdmin.deleteLabelMessage')
          }
          confirmLabel={t('common.delete')}
          pending={remove.isPending}
          onCancel={() => {
            setDeleting(null);
          }}
          onConfirm={() => {
            remove.mutate({ labelId: label.id }, { onSuccess: onClose });
          }}
        />
      )}
    </Dialog>
  );
}
